import { FC, useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import { ToucanColors } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import InstallExtensionAlert from '~/components/shared/InstallExtensionAlert';
import { Origin } from '~/constants/events';
import Errors from '~/helpers/errors';
import { useExtension } from '~/hooks/useExtension';
import { useExtensionConfig } from '~/hooks/useExtensionConfig';
import { useFirefoxExtension } from '~/hooks/useFirefoxExtension';
import { useMobileDeviceUserAgent } from '~/hooks/useMobileDeviceUserAgent';
import { BrowserName } from '~/lib/extension-config';

import UserDashboardContainer from '../Container';

import AccountInformationSection from './AccountInformationSection';
import AllowAccessAlert, { FirefoxAllowAccessAlert } from './AllowAccessAlert';
import EditAccountInfo from './EditAccountInfo';
// import SubscriptionStatusSection from './SubscriptionStatusSection';

const StandardExtensionContent = () => {
  const { isInstalled, hasGlobalPermissions, requestGlobalPermissions } = useExtension();
  return isInstalled ? (
    <AllowAccessAlert
      hasGlobalPermissions={hasGlobalPermissions}
      onRequestGlobalPermissions={() => requestGlobalPermissions(Origin.Dashboard)}
    />
  ) : (
    <InstallExtensionAlert />
  );
};

const FirefoxExtensionContent = () => {
  const { isInstalledInFirefox, hasGlobalPermissionsInFirefox, refetch } = useFirefoxExtension();
  const intervalId = useRef<number>();

  // Update the page if the user grants global permissions.
  useEffect(() => {
    if (!hasGlobalPermissionsInFirefox && refetch) {
      intervalId.current = window.setInterval(refetch, 1000);
    }
    return () => {
      window.clearInterval(intervalId.current);
    };
  }, [hasGlobalPermissionsInFirefox, refetch]);

  if (!isInstalledInFirefox) {
    return <InstallExtensionAlert />;
  }
  return hasGlobalPermissionsInFirefox ? null : <FirefoxAllowAccessAlert />;
};

const Settings: FC = () => {
  // const [isSubscriptionReadyToRefetch, setIsSubscriptionReadyToRefetch] = useState(false);
  const isContentVisible = useState(true); // Flag to hide/show page content while Google Auth and subscription refresh is doing its thing. Without it, it shows the user as anonymous briefly.
  const theme = useTheme();
  const isMobileUserAgent = useMobileDeviceUserAgent();
  const [isEditAccountInfoForMobileShown, setIsEditAccountInfoForMobileShown] = useState(false);
  const [editFieldType, setEditFieldType] = useState<string | null>(null);
  const { browserName } = useExtensionConfig();
  const { t } = useTranslation('dashboard');

  const isFirefox = browserName === BrowserName.Firefox;

  // const forceRefetchSubscription = () => {
  //  setIsSubscriptionReadyToRefetch(true); // Flag to activate refresh in SubscriptionStatusSection component
  // };

  if (isMobileUserAgent && isEditAccountInfoForMobileShown) {
    return (
      <UserDashboardContainer title={t('dashboardSettings')} pageError={Errors.UserDashboardSettingsPageError}>
        <div
          css={css`
            background: ${ToucanColors.white};
            position: absolute;
            top: 0;
            left: 0;
            padding-top: ${theme.spacing(11)};
            padding-left: ${theme.spacing(2)};
            padding-right: ${theme.spacing(2)};
            width: 100%;
          `}
        >
          <EditAccountInfo
            editType={editFieldType ?? ''}
            setIsEditAccountInfoForMobileShown={setIsEditAccountInfoForMobileShown}
          />
        </div>
      </UserDashboardContainer>
    );
  }

  // <SubscriptionStatusSection
  //  isReadyToRefetch={isSubscriptionReadyToRefetch}
  //  onRefetchComplete={() => {
  //    setIsSubscriptionReadyToRefetch(false);
  //    setIsContentVisible(true);
  //  }}
  /// >

  return (
    <UserDashboardContainer title={t('dashboardSettings')} pageError={Errors.UserDashboardSettingsPageError}>
      {isContentVisible && (
        <>
          {isFirefox ? <FirefoxExtensionContent /> : <StandardExtensionContent />}

          <AccountInformationSection
            onLoginSignup={() => {}}
            setIsEditAccountInfoForMobileShown={setIsEditAccountInfoForMobileShown}
            setEditFieldType={setEditFieldType}
          />
        </>
      )}
    </UserDashboardContainer>
  );
};

export default Settings;
